import type { ConsentAcceptance } from "../../domain/schemas";

const SHADOW_PRINCIPLES = [
  "No automatic publishing — Syntha handoffs stay draft and require manual review.",
  "SHADOW Signal is readiness guidance, not legal advice or endorsement.",
  "Paid depth and live providers remain gated until approvals clear.",
  "Artist creative material is review evidence, not training data.",
  "Rights and disclosure must be explicit before any escalation path opens."
];

export function ConsentStep({
  consent,
  setConsent,
  onBack,
  onRun
}: {
  consent: ConsentAcceptance;
  setConsent: (fn: (current: ConsentAcceptance) => ConsentAcceptance) => void;
  onBack: () => void;
  onRun: () => void;
}) {
  const canRun = consent.analysisConsent && consent.rightsAttestation && consent.aiDisclosureAcknowledged;

  const CONSENT_LABELS: [keyof ConsentAcceptance, string, string][] = [
    ["analysisConsent", "Analysis consent", "I understand SHADOW will analyze my creative material for release readiness."],
    ["rightsAttestation", "Rights attestation", "I attest that I have the rights to share the material I am submitting."],
    ["aiDisclosureAcknowledged", "Artificial Intelligence disclosure", "I acknowledge that SHADOW uses artificial intelligence to generate this evaluation."],
    ["marketingOptIn", "Marketing updates (optional)", "Receive occasional updates about SHADOW and VaultSpark Studios."],
    ["benchmarkOptIn", "Genre benchmark contribution (optional)", "Allow my Signal readiness data to contribute anonymously to genre performance benchmarks. No personal information is shared."]
  ];

  return (
    <section className="workspace">
      <article className="panel">
        <p className="panelKicker">02 / Consent primitives</p>
        <h2>Release pressure stops here.</h2>
        <p>SHADOW cannot escalate unclear rights, missing disclosure, or uncertain consent. Read each statement and check to confirm.</p>
        {CONSENT_LABELS.map(([key, label, description]) => (
          <label key={key} className="check" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 12px", alignItems: "start", marginBottom: "14px" }}>
            <input
              type="checkbox"
              checked={Boolean(consent[key as keyof ConsentAcceptance])}
              onChange={(event) => setConsent((current) => ({ ...current, [key]: event.target.checked }))}
              style={{ marginTop: "3px" }}
            />
            <span>
              <strong style={{ display: "block", fontSize: "0.9rem" }}>{label}</strong>
              <span style={{ color: "var(--text-soft)", fontSize: "0.82rem" }}>{description}</span>
            </span>
          </label>
        ))}
        {!canRun && (
          <p style={{ color: "var(--text-soft)", fontSize: "0.82rem", margin: "8px 0 16px" }}>
            Analysis consent, rights attestation, and AI disclosure are required to continue.
          </p>
        )}
        <div className="formActions splitActions">
          <button type="button" className="secondaryButton" onClick={onBack}>Back</button>
          <button type="button" onClick={onRun} disabled={!canRun} style={!canRun ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Enter The Dark Room</button>
        </div>
      </article>
      <article className="panel">
        <p className="panelKicker">Safety boundary</p>
        <h2>How SHADOW protects you.</h2>
        <ul>
          {SHADOW_PRINCIPLES.map((principle) => (
            <li key={principle} style={{ color: "var(--text-muted)", marginBottom: "10px" }}>{principle}</li>
          ))}
        </ul>
      </article>
    </section>
  );
}
